/**
 * ForwardRunRateLine — the ONE forward run-rate callout, shared by the two
 * dossier surfaces that show it (Lane 382 item 3).
 *
 * Ryan (staging QA, 2026-07-25): "the same callout ... use the same red font as
 * we're doing up there. It looks a little few too many font colours. If it's
 * saying the same thing, use the same font ... use the same terminology on the
 * price-vs-market as on the price summary, because it's the same thing."
 *
 * Before this component the two callsites each built the line themselves:
 *   - PriceSummaryBand's trend tiles  — dollar tinted `var(--up)`   (red)
 *   - SupplierDetail's Price vs Market card — dollar tinted `var(--warn)` (amber)
 * with two separate local dollar formatters that happened to agree. Same
 * sentence, two colours, two formatters — a drift waiting to happen and, on
 * screen, two different-looking statements of one fact. One component now owns
 * the wording, the formatter and the colour, so the two surfaces agree BY
 * CONSTRUCTION rather than by two authors keeping strings in step.
 *
 * The colour is `var(--up)` — the dossier's cost-increase red (the price band's
 * choice, i.e. Ryan's "up there"). It is honest here by construction: the
 * engine emits `forward_run_rate_surface_usd` ONLY for the negotiation case
 * (sign-agreeing with the headline % AND a cost increase), so a value reaching
 * this component is always an increase (P-3 / Lane 357, pairing rules P3/P6).
 *
 * "(projection)" is KEPT — pairing rule P4: the backward "cost you so far"
 * dollar and this forward dollar can be read side by side on the same band, and
 * this token is what distinguishes recorded cost from projection.
 *
 * The DOLLAR FORMAT is `fmtSignedDollars` from utils/priceSectionNotes — the
 * band already used it; SupplierDetail's byte-equivalent local `fmtForwardDollars`
 * is retired in favour of it, so one rounding rule serves both.
 */
import React from 'react';
import { fmtSignedDollars } from '../utils/priceSectionNotes';

/**
 * Props:
 *   usd     — the forward run-rate dollars (`forward_run_rate_surface_usd`).
 *             Callers render this component only when that key is a number;
 *             a non-number here renders nothing rather than "—", so an absent
 *             pairing key can never produce a stray sentence.
 *   months  — the window the projection covers (6 / 12 / 24).
 *   style   — optional inline style for the wrapper (the band's tiles use
 *             inline styles).
 *   className — optional class for the wrapper (SupplierDetail's card uses
 *             page-scoped `.sd` classes).
 *   testId  — optional data-testid for the wrapper.
 */
export default function ForwardRunRateLine({ usd, months, style, className, testId }) {
  if (typeof usd !== 'number' || !Number.isFinite(usd)) return null;
  return (
    <div style={style} className={className} data-testid={testId}>
      At today&rsquo;s prices, same volume:{' '}
      {/* `data-tone` is the assertable twin of the colour: jsdom drops var()
          colours out of the style attribute, so a `style` assertion on the tint
          is VACUOUS (it passes whatever the colour is — verified by running it).
          Both callsites render THIS component, so a callsite that hand-rolled
          its own span again would be missing this attribute and fail the pin. */}
      <span
        data-tone="cost-increase"
        style={{ color: 'var(--up)', fontWeight: 'var(--font-weight-semibold)' }}
      >
        {fmtSignedDollars(usd)}
      </span>{' '}
      over the next {months} months (projection)
    </div>
  );
}
